import React, { createContext, useContext, useState, useEffect } from "react";
import { auth } from "../firebaseConfig";

// Define a interface for the user object
interface User {
  uid: string;
  name: string;
  email: string;
  phone?: string;
  birthDate?: string;
  onboardingCompleted?: boolean;
  token?: string;  // Add token to the interface
  accessLevel?: number;
  // Add other user properties as needed
}

// Define the context type
interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  refreshToken: () => Promise<string | null>;
  logout: () => void;
}

// Create context with initial value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Define props type for AuthProvider
interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (saved) {
      const parsedUser = JSON.parse(saved);
      return { ...parsedUser, token };
    }
    return null;
  });

  // Function to refresh the token
  const refreshToken = async (): Promise<string | null> => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return null;
      }

      // Force token refresh
      const newToken = await currentUser.getIdToken(true);
      
      // Update user state with new token
      setUser(prevUser => {
        if (prevUser) {
          const updatedUser = { ...prevUser, token: newToken };
          localStorage.setItem("token", newToken);
          localStorage.setItem("user", JSON.stringify(updatedUser));
          return updatedUser;
        }
        return null;
      });

      return newToken;
    } catch (error) {
      console.error("Error refreshing token:", error);
      return null;
    }
  };

  // Function to logout
  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  };

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
      if (user.token) {
        localStorage.setItem("token", user.token);
      }
    } else {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, refreshToken, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
